/**
 * The answers every update surface reads, derived in one place so the updates
 * dots, the server updates and the server rows cannot disagree.
 *
 * A server is skipped by a plan when it runs something newer than the target:
 * the updater never downgrades, so the cloud counts it as done.
 */

import type {
    AvailableUpdates, Location, MaintenanceWindow, ServerUpdateStatus, UpdatePlan,
} from '@/types/servers';
import type { FleetServer } from '@/types/servers';
import { UpdatePlanState } from '@/types/servers';
import type { ReleaseView, UpdatesView } from '@/types/updates';

import { compareVersions, runsAtLeast } from './version';

export const DEFAULT_WINDOW: MaintenanceWindow = { start: '02:00', end: '04:00' };

/** What the fleet is offered before the first read answers. */
export const NO_UPDATES: AvailableUpdates = {
    candidate: null, plan: null, maintenanceWindow: null,
};

interface UpdatesInput {
    availableValue: AvailableUpdates;
    plans: UpdatePlan[];
    locations: Location[];
    servers: FleetServer[];
}

const getVersion = (server: FleetServer): string => server.updateStatus?.version ?? '';

export function deriveUpdatesView({
    availableValue, plans, locations, servers,
}: UpdatesInput): UpdatesView {
    const { candidate, plan, maintenanceWindow } = availableValue;
    const target = plan?.targetVersion || candidate?.version || null;
    const on = (server: FleetServer): boolean => (
        target !== null && runsAtLeast(getVersion(server), target)
    );
    return {
        servers,
        locationNames: Object.fromEntries(locations.map((l) => [l.id, l.name])),
        candidate,
        plan,
        window: maintenanceWindow ?? DEFAULT_WINDOW,
        target,
        skipped: target === null
            ? []
            : servers.filter((s) => compareVersions(getVersion(s), target) > 0),
        downloaded: servers.filter(
            (s) => on(s) || (target !== null && s.updateStatus?.downloadedVersion === target),
        ).length,
        updated: servers.filter(on).length,
        // The API lists plans newest first.
        lastClosed: plans.find((p) => Boolean(p.cancelledAt || p.completedAt)) ?? null,
    };
}

/**
 * The fleet waits on an admin when it could move and nobody planned it, or when
 * its plan is ready to install. A plan in progress waits on the servers.
 */
export function needsAttention(
    { candidate, plan }: Pick<AvailableUpdates, 'candidate' | 'plan'>, skippedText: string | null,
): boolean {
    if (plan) {
        return plan.state === UpdatePlanState.Ready;
    }
    return candidate !== null && candidate.version !== skippedText;
}

export function releaseView(view: UpdatesView, skippedText: string | null): ReleaseView {
    const version = view.target;
    const total = view.servers.length;
    if (version === null) {
        return {
            version: null, updated: total, total, canSkip: false, isSkipped: false,
        };
    }
    return {
        version,
        updated: view.servers.filter((s) => runsAtLeast(getVersion(s), version)).length,
        total,
        canSkip: view.plan?.targetVersion !== version,
        isSkipped: skippedText === version,
    };
}

/** A skip outlives its release otherwise, and would hide the next one's badge. */
export function isStaleSkip(availableValue: AvailableUpdates,
    skippedText: string | null): boolean {
    return skippedText !== null && availableValue.candidate?.version !== skippedText;
}

/** Why a server will restart and when, or null when the plan leaves it alone. */
export function getServerPlanSentence(
    status: ServerUpdateStatus | null, view: UpdatesView | null,
): string | null {
    const plan = view?.plan;
    if (!status || !plan || runsAtLeast(status.version, plan.targetVersion)) {
        return null;
    }
    const pendingState = status.downloadedVersion === plan.targetVersion ? 'downloaded'
        : 'planned';
    const whenText = plan.installNow
        ? 'install requested'
        : `installs ${plan.maintenanceWindow.start}–${plan.maintenanceWindow.end}`;
    return `${plan.targetVersion} ${pendingState}, ${whenText}`;
}

const FAILED_STAGE: Record<string, string> = {
    downloadFailed: 'Download of {v} failed',
    verificationFailed: 'Download of {v} did not verify',
    installFailed: 'Install of {v} failed',
    unhealthy: '{v} installed, but the server did not come up',
};

function getFailureText(status: ServerUpdateStatus): string {
    const version = status.failureVersion || status.targetVersion || 'the update';
    const stage = (FAILED_STAGE[status.lastOutcome] ?? 'Update to {v} failed')
        .replace('{v}', version);
    return status.lastReason ? `${stage}: ${status.lastReason}` : stage;
}

/** What is happening to a server's version, as a sentence for a table cell or a tooltip. */
export function updateStatusText(
    status: ServerUpdateStatus | null, view: UpdatesView | null,
): string {
    if (!status || status.state === 'unknown') {
        return 'No report from this server yet';
    }
    const target = status.targetVersion || view?.target || 'the update';
    switch (status.state) {
        case 'updating':
            return `Installing ${target}`;
        case 'stalled':
            return `Install of ${target} stopped responding`;
        case 'failed':
            return getFailureText(status);
        case 'planned':
        case 'downloaded': {
            const sentence = getServerPlanSentence(status, view);
            return sentence ? `Update to ${sentence}` : 'Update planned';
        }
        case 'behind':
            return view?.candidate
                ? `${view.candidate.version} is available`
                : 'An update is available';
        default:
            return 'Up to date';
    }
}

export function hidesBehindBadge(
    status: ServerUpdateStatus | null, view: UpdatesView | null, skippedText: string | null,
): boolean {
    return status?.state === 'behind' &&
        skippedText !== null &&
        view?.candidate?.version === skippedText;
}
